/**
 * Client-only preference: whether the header "Sync Data" button auto-triggers
 * a sync when you navigate to a data-consuming page (market data / analytics /
 * simulators / grafana), or ONLY syncs when clicked.
 *
 * Stored in localStorage (like `settings.savedSimsLimit`) since it is a pure UI
 * behaviour toggle with no server relevance. A small custom-event pub/sub makes
 * the value reactive WITHIN the same tab (the native `storage` event only fires
 * in OTHER tabs), so flipping the switch on the Settings page updates the
 * header button immediately.
 *
 * Note: this only governs the on-NAVIGATION auto-sync. The correctness-driven
 * syncs (before a simulation runs, after an outcome/order edit, manual clicks)
 * are unaffected and always run.
 */
import { useCallback, useEffect, useState } from "react";

export const AUTO_SYNC_ON_NAV_KEY = "settings.autoSyncOnNav";
/** Default ON to preserve the behaviour shipped with the DB-split feature. */
export const AUTO_SYNC_ON_NAV_DEFAULT = true;

const EVENT_NAME = "autoSyncOnNav:changed";

export function getAutoSyncOnNav(): boolean {
  try {
    const raw = localStorage.getItem(AUTO_SYNC_ON_NAV_KEY);
    if (raw == null) return AUTO_SYNC_ON_NAV_DEFAULT;
    return raw === "1" || raw === "true";
  } catch {
    return AUTO_SYNC_ON_NAV_DEFAULT;
  }
}

export function setAutoSyncOnNav(enabled: boolean): void {
  try {
    localStorage.setItem(AUTO_SYNC_ON_NAV_KEY, enabled ? "1" : "0");
  } catch {
    /* localStorage unavailable — fall through to the in-tab broadcast */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent<boolean>(EVENT_NAME, { detail: enabled }));
  }
}

/** Reactive `[enabled, setEnabled]` backed by localStorage + same-tab events. */
export function useAutoSyncOnNav(): [boolean, (enabled: boolean) => void] {
  const [enabled, setEnabled] = useState<boolean>(getAutoSyncOnNav);

  useEffect(() => {
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<boolean>).detail;
      setEnabled(typeof detail === "boolean" ? detail : getAutoSyncOnNav());
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === AUTO_SYNC_ON_NAV_KEY) setEnabled(getAutoSyncOnNav());
    };
    window.addEventListener(EVENT_NAME, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const set = useCallback((next: boolean) => setAutoSyncOnNav(next), []);
  return [enabled, set];
}
